import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { HugeiconsIcon } from "@hugeicons/react";
import { DiscoverCircleIcon, Location01Icon, Setting07Icon } from "@hugeicons/core-free-icons";
import { getServicesData } from './services/servicesData';
import { useLanguage } from '../../contexts/LanguageContext';
import CountUpAnimation from '../CountUpAnimation';

const About: React.FC = () => {
  const { t, isRTL } = useLanguage();
  const showAllServices = false;
  const INITIAL_SERVICE_COUNT = 6;
  const showStatistics = false;
  
  const servicesData = useMemo(() => getServicesData(t), [t]);

  const visibleServices = showAllServices
    ? servicesData
    : servicesData.slice(0, INITIAL_SERVICE_COUNT);
  const servicesGridClass = useMemo(() => {
    const count = visibleServices.length;

    if (count <= 1) return "grid-cols-1 max-w-sm";
    if (count === 2) return "grid-cols-1 md:grid-cols-2 max-w-4xl";
    if (count === 3) return "grid-cols-1 md:grid-cols-3 max-w-7xl";
    if (count === 4) return "grid-cols-1 md:grid-cols-2 max-w-5xl";

    return "grid-cols-1 md:grid-cols-2 xl:grid-cols-3 max-w-7xl";
  }, [visibleServices.length]);

  return (
    <div className="min-h-screen bg-gray-50" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Slider Section - Hidden */}
      {/* <section className="relative h-[300px] md:h-[400px] overflow-hidden">
        {slides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <div
              className="w-full h-full bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.bgImage})` }}
            >
            </div>
          </div>
        ))}
        
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
          <div className="flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-sa-600'
                    : 'bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        </div>
      </section> */}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-16">
        {/* Ministry Services Section */}
        <section className="mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              خدمات المساجد الإلكترونية
            </h2>
          </div>
          
          <div className="flex justify-center mb-8">
            <div className={`grid auto-rows-fr gap-8 w-full ${servicesGridClass}`}>
            {visibleServices.map((service) => (
              <Link
                key={service.id}
                to={service.link}
                className="group block h-full focus:outline-none focus-visible:ring-4 focus-visible:ring-sa-200 rounded-2xl"
              >
                <article className="bg-white rounded-2xl shadow-lg border border-gray-100 p-7 md:p-8 hover:shadow-xl transition-all duration-300 group-hover:-translate-y-1 flex flex-col h-full min-h-[360px]">
                  {/* Icon */}
                  <div className={`w-12 h-12 flex items-center justify-center mb-4 ${isRTL ? "self-end" : "self-start"}`}>
                    {service.icon === "land" ? (
                      <span className="text-sa-600" aria-hidden="true">
                        <HugeiconsIcon icon={DiscoverCircleIcon} size={28} color="currentColor" strokeWidth={1.8} />
                      </span>
                    ) : service.icon === "search" ? (
                      <span className="text-sa-600" aria-hidden="true">
                        <HugeiconsIcon icon={Location01Icon} size={28} color="currentColor" strokeWidth={1.8} />
                      </span>
                    ) : service.icon === "maintenance" ? (
                      <span className="text-sa-600" aria-hidden="true">
                        <HugeiconsIcon icon={Setting07Icon} size={28} color="currentColor" strokeWidth={1.8} />
                      </span>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" className="text-sa-600">
                        <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                  </div>

                  {/* Title */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3 text-right group-hover:text-sa-700 transition-colors duration-300">
                    {service.title}
                  </h3>

                  {/* Description */}
                  <p className="text-gray-600 mb-4 text-right text-sm leading-relaxed flex-grow">
                    {service.description}
                  </p>

                  <div className="flex items-center justify-between mt-auto pt-4 border-t border-gray-100">
                    <span className="text-xs px-3 py-1 bg-sa-100 text-sa-700 rounded-lg font-semibold">
                      {service.category}
                    </span>
                    <svg
                      className="w-5 h-5 text-sa-400 group-hover:text-sa-600 transition-colors duration-300"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M9 18l-6-6 6-6" />
                      <path d="M3 12h18" />
                    </svg>
                  </div>
                </article>
              </Link>
            ))}
            </div>
          </div>
        </section>
 
        {/* Stats Section */}
        {showStatistics && (
          <section className="mb-16">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('statistics')}
              </h2>
            </div>

            <div className="flex justify-center">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl">
                {[{
                  title: t('mosquesCount'),
                  value: 87140,
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-10 h-10 text-sa-600">
                      <path d="M12 3l6 4v4h2v10h-6v-6h-4v6H4V11h2V7l6-4z" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M12 11h.01" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )
                }, {
                  title: t('mosquePersonnel'),
                  value: 100697,
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-10 h-10 text-sa-600">
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4z" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M4 21v-1c0-2.761 2.239-5 5-5h6c2.761 0 5 2.239 5 5v1" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )
                }, {
                  title: t('availableLands'),
                  value: 150,
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-10 h-10 text-sa-600">
                      <path d="M3 19h18" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M5 19v-5l3-2 4 3 4-2 3 2v4" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M8 9h8" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M10 5h4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )
                }, {
                  title: t('fieldVisits'),
                  value: 2362458,
                  icon: (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="w-10 h-10 text-sa-600">
                      <path d="M12 2c3.866 0 7 2.925 7 6.533 0 4.209-3.401 8.56-6.113 11.146a1.22 1.22 0 0 1-1.774 0C8.401 17.093 5 12.742 5 8.533 5 4.925 8.134 2 12 2z" strokeLinecap="round" strokeLinejoin="round" />
                      <circle cx="12" cy="9" r="2" />
                    </svg>
                  )
                }].map((stat) => (
                  <div key={stat.title} className="bg-white border border-sa-100 rounded-2xl p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-shadow duration-300">
                    <div className="mb-4 text-sa-600">
                      {stat.icon}
                    </div>
                    <p className="text-sm text-sa-600 font-semibold mb-1">{stat.title}</p>
                    <CountUpAnimation 
                      end={stat.value} 
                      duration={2.5}
                      className="text-3xl font-bold text-gray-900"
                    />
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

      </div>
    </div>
  );
};

export default About;